import { FilterGroup, FilterLogic, RepositoryCurrency, Sort, TransactionSettingsAsset } from '@types'
import { AssetClass } from '@/entities/assets'
import { TagClass } from '@/entities/tags'

import { ASSET_CURRENCY_TYPE } from '@/entities/assets/utils/const'
import { TAG_FIELD } from '@/entities/tags/utils/const'

import { useAssetsBunchStore } from '@/store/assets/bunch'
import { IMPORT_HEADERS_KEYS, IMPORT_HEADERS_EXAMPLE, IMPORT_LEGACY_HEADERS } from './const'

// Dynamically generate header mapping from constants
const createHeaderMapping = (): Record<string, string> => {
  const mapping: Record<string, string> = {}

  const currentHeaders = IMPORT_HEADERS_EXAMPLE.split(',')
  const headerKeys = IMPORT_HEADERS_KEYS.split(',')

  currentHeaders.forEach((header, index) => {
    if (headerKeys[index]) {
      mapping[header.toLowerCase()] = headerKeys[index]
    }
  })

  IMPORT_LEGACY_HEADERS.forEach(legacyFormat => {
    const legacyHeaders = (legacyFormat as string).split(',')
    legacyHeaders.forEach((header, index) => {
      if (headerKeys[index]) {
        mapping[header.toLowerCase()] = headerKeys[index]
      }
    })
  })

  return mapping
}

const MAPPING = createHeaderMapping()

/**
 * Normalizes CSV headers to support both old and new formats
 * @param data Object with CSV headers as keys
 * @returns Object with normalized field names as keys
 */
export const normalizeImportData = (data: Record<string, any>): Record<string, any> => {
  const normalizedData: Record<string, any> = {}

  Object.keys(data).forEach(key => {
    const normalizedKey = MAPPING[key] || key
    normalizedData[normalizedKey] = data[key]
  })

  return normalizedData
}

const checkIfAssetIsset = (
  assetsBunchStore: ReturnType<typeof useAssetsBunchStore>,
  value?: string,
) => {
  return (
    value &&
    (assetsBunchStore.getElementByName(value) ||
      assetsBunchStore.getElementByTicker(value))
  )
}

export const prepareAsset =
  (
    assetsBunchStore: ReturnType<typeof useAssetsBunchStore>,
    categoriesList: TagClass[],
    currenciesList: RepositoryCurrency[],
    currency: string,
    assets: string[][],
    accounts: string[][],
    currencies: string[][],
  ) =>
    (value: string, settings?: TransactionSettingsAsset) => {
      if (!settings) return
      let name = value
      let asset = checkIfAssetIsset(assetsBunchStore, value)
      if (!asset && (settings.value || settings.default)) {
        name = settings.value || settings.default || ''
        asset = checkIfAssetIsset(assetsBunchStore, name)
      }
      if (asset) return asset.id

      const isAlreadyExists =
      assets.find(item => item[1] === name) ||
      accounts.find(item => item[0] === name) ||
      currencies.find(item => item[1] === name)

      if (isAlreadyExists) return

      let type = 'other'
      if (currenciesList.find(item => item.ticker === name)) {
        type = ASSET_CURRENCY_TYPE
      } else if (settings.includes) {
        type = settings.includes[0]
      }

      let instance: AssetClass | null = new AssetClass({ type, name })
      if (instance.isAccount) {
        const tag = instance.isIncomeAccount ? 'Income' : 'Expense'
        const category = categoriesList.find(
          instance => instance.field<string>(TAG_FIELD.TAG_VALUE).value === tag,
        )
        const subcategory = categoriesList.find(
          instance =>
            instance.field<string>(TAG_FIELD.TAG_VALUE).value === `Other ${tag}`,
        )
        accounts.push([name, category?.id || '', subcategory?.id || ''])
      } else if (instance.isCurrency) {
        currencies.push([type, name, currency])
      } else {
        assets.push([type, name, name, currency, ''])
      }

      instance = null

      return
    }

export const mapTransactionsSort = (sort?: Sort[]) => {
  const value = sort?.map(item => {
    let field = item.field
    let pos_id
    if (item.field.includes('__pos_id-')) {
      ;[field, pos_id] = item.field.split('__pos_id-')
    }
    return {
      field,
      sortDirection: item.sortDirection,
      index: item.index,
      pos_id,
    }
  })
  return value?.length ? value : null
}

export const mapTransactionsFilter = (value?: FilterGroup) => {
  const params = value?.params?.map(param => {
    let { field } = param
    let pos_id
    if (field?.includes('__pos_id-')) {
      ;[field, pos_id] = field.split('__pos_id-')
    }

    return {
      ...param,
      field,
      ...(pos_id ? { pos_id } : {}),
    }
  }).filter(param => !!param.field)
  return params?.length ? {
    logic: value?.logic || FilterLogic.AND,
    params,
  } : null
}
